import {
  Dispatch,
  FormEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@navikt/aksel-icons';
import z from 'zod';
import { Stepper } from './Stepper/Stepper';
import { createSurveySchema } from './survey-form-card-validation.schema';
import { Team } from '../../api/queries';
import {
  FIELD_IDS,
  STEP_FIELDS,
  SurveyFormCardStepsEnum,
  SurveyFormCardUtils,
} from '../../utils/SurveyFormCardUtils/SurveyFormCard.utils';
import { SurveySetup } from './SurveySetup/SurveySetup';
import { SurveyDistributionChannels } from './SurveyDistributionChannels/SurveyDistributionChannels';
import { SurveyTimeline } from './SurveyTimeline/SurveyTimeline';
import { SurveyFormSummary } from './SurveyFormSummary/SurveyFormSummary';
import classes from './SurveyFormCard.module.css';

export type Step = {
  id: SurveyFormCardStepsEnum;
  title: string;
  completed: boolean;
  inProgress: boolean;
  stepNumber: number;
};

export type SurveyFormData = z.infer<ReturnType<typeof createSurveySchema>>;

export type SurveyFormMode = 'create' | 'edit';

type SurveyFormCardProps = {
  formData: SurveyFormData;
  setFormData: Dispatch<SetStateAction<SurveyFormData>>;
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  teams: Team[];
  onCreateSurvey: (form: SurveyFormData, activeButton?: () => void) => void;
  mode?: SurveyFormMode;
  backTo: string;
  backLabel: string;
  submitError?: { name: string; message: string } | null;
  /** «Kom i gang»: a banner with a tip for each step, and the template chosen for the user */
  guidance?: { banner: (step: SurveyFormCardStepsEnum) => ReactElement; preselectTemplate: boolean };
  /** Something the user should know about the survey being made, shown above the steps without stopping them */
  notice?: ReactNode;
};

const LAST_STEP = SurveyFormCardUtils.getInitialSteps().length - 1;

const getSteps = (currentStep: number, maxStep: number): Step[] =>
  SurveyFormCardUtils.getInitialSteps().map((step, index) => ({
    ...step,
    inProgress: index === currentStep,
    completed: index < currentStep || (index <= maxStep && index !== currentStep),
  }));

export const SurveyFormCard = ({
  formData,
  setFormData,
  teams,
  currentStep,
  setCurrentStep,
  onCreateSurvey,
  mode = 'create',
  backTo,
  backLabel,
  submitError,
  guidance,
  notice,
}: SurveyFormCardProps) => {
  const { t, i18n } = useTranslation();
  const surveySchema = useMemo(() => createSurveySchema(t), [t]);
  const isEdit = mode === 'edit';

  // Everything in an existing survey is already filled in, so all steps are reachable.
  const [maxStep, setMaxStep] = useState<number>(
    isEdit ? LAST_STEP : currentStep,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [errorTick, setErrorTick] = useState(0);
  const [disableSendButton, setDisableSendButton] = useState<boolean>(false);

  const pageRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const previousStep = useRef(currentStep);

  const steps = useMemo(
    () => getSteps(currentStep, maxStep),
    // Step titles are translated, so rebuild them when the language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep, maxStep, i18n.language],
  );
  const currentStepId = steps[currentStep].id;
  const infoText = SurveyFormCardUtils.getInfoText(currentStepId);

  useEffect(() => {
    if (previousStep.current === currentStep) return;
    previousStep.current = currentStep;
    pageRef.current?.scrollIntoView?.({ block: 'start' });
    headingRef.current?.focus({ preventScroll: true });
  }, [currentStep]);

  useEffect(() => {
    if (errorTick > 0) errorSummaryRef.current?.focus();
  }, [errorTick]);

  /** Returns the schema errors for the given steps, first error per field. */
  const validateSteps = (stepIds: SurveyFormCardStepsEnum[]) => {
    const fields = new Set<string>(stepIds.flatMap((id) => STEP_FIELDS[id]));
    const result = surveySchema.safeParse(formData);
    const stepErrors: Record<string, string> = {};
    if (!result.success) {
      result.error.issues.forEach((issue) => {
        const field = String(issue.path[0] ?? '');
        if (fields.has(field) && !stepErrors[field]) {
          stepErrors[field] = issue.message;
        }
      });
    }
    return stepErrors;
  };

  const showErrors = (newErrors: Record<string, string>) => {
    setErrors(newErrors);
    setErrorTick((tick) => tick + 1);
  };

  const goToStep = (step: number) => {
    setErrors({});
    setCurrentStep(step);
    setMaxStep((max) => Math.max(max, step));
  };

  const handleStepClick = (step: number) => {
    if (step <= maxStep) goToStep(step);
  };

  const handleSubmit = () => {
    const allErrors = validateSteps(steps.map((step) => step.id));
    if (Object.keys(allErrors).length) {
      const firstInvalidStep = steps.findIndex((step) =>
        STEP_FIELDS[step.id].some((field) => allErrors[field]),
      );
      // Only the fields on the step the user is sent to can be linked to and fixed
      const stepErrors = Object.fromEntries(
        Object.entries(allErrors).filter(([field]) =>
          (STEP_FIELDS[steps[firstInvalidStep].id] as string[]).includes(field),
        ),
      );
      goToStep(firstInvalidStep);
      showErrors(stepErrors);
      return;
    }

    setDisableSendButton(true);
    onCreateSurvey(formData, () => {
      setDisableSendButton(false);
    });
  };

  const handleNext = (event: FormEvent) => {
    event.preventDefault();

    if (currentStep === LAST_STEP) {
      handleSubmit();
      return;
    }

    const stepErrors = validateSteps([currentStepId]);
    if (Object.keys(stepErrors).length) {
      showErrors(stepErrors);
      return;
    }

    goToStep(currentStep + 1);
  };

  const handleChange = (
    name: string,
    value: string | Array<string> | boolean | number | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const focusField = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    const element = document.getElementById(id);
    if (!element) return;
    event.preventDefault();
    element.focus();
    element.scrollIntoView?.({ block: 'center' });
  };

  const stepComponentMap: Record<SurveyFormCardStepsEnum, ReactElement> = {
    [SurveyFormCardStepsEnum.surveySetup]: (
      <SurveySetup
        handleChange={handleChange}
        formData={formData}
        teams={teams}
        errors={errors}
        preselectTemplate={guidance?.preselectTemplate}
      />
    ),
    [SurveyFormCardStepsEnum.surveyDistributionChannels]: (
      <SurveyDistributionChannels formData={formData} />
    ),
    [SurveyFormCardStepsEnum.surveyTimeline]: (
      <SurveyTimeline
        formData={formData}
        errors={errors}
        handleChange={handleChange}
        requirePlanning={isEdit}
      />
    ),
    [SurveyFormCardStepsEnum.surveySummary]: (
      <SurveyFormSummary
        formData={formData}
        teamName={teams.find((team) => team.id === formData.team)?.name || ''}
        onEditStep={handleStepClick}
      />
    ),
  };

  const errorEntries = Object.entries(errors);

  const submitLabel = isEdit
    ? t('surveyCreation.buttons.saveChanges')
    : formData.isPlanning
      ? t('surveyCreation.buttons.planSurvey')
      : t('surveyCreation.buttons.createSurvey');

  return (
    <div className={classes.page} ref={pageRef}>
      <div aria-hidden="true" className={classes.backdrop} />
      <div className={classes.content}>
        <Link to={backTo} className={`ds-link ${classes.backLink}`}>
          <ArrowLeftIcon aria-hidden="true" fontSize="1.25rem" />
          {backLabel}
        </Link>

        <header className={classes.header}>
          <span className={`ds-label ${classes.eyebrow}`} data-size="sm">
            {isEdit
              ? t('surveyCreation.wizard.eyebrowEdit')
              : t('surveyCreation.wizard.eyebrowCreate')}
          </span>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className={`ds-heading ${classes.title}`}
            data-size="xl"
          >
            {infoText.title}
          </h1>
          <p className={`ds-paragraph ${classes.lead}`} data-size="lg">
            {infoText.text}
          </p>
        </header>

        {guidance?.banner(currentStepId)}

        {notice}

        <Stepper steps={steps} maxStep={maxStep} onStepClick={handleStepClick} />

        <form noValidate onSubmit={handleNext} className={classes.card}>
          <div className={classes.cardBody}>
            {errorEntries.length > 0 && (
              <div
                className="ds-error-summary"
                tabIndex={-1}
                ref={errorSummaryRef}
              >
                <h2 className="ds-heading" data-size="2xs">
                  {t('surveyCreation.wizard.errorSummaryTitle')}
                </h2>
                <ul className="ds-list">
                  {errorEntries.map(([field, message]) => {
                    const id = FIELD_IDS[field as keyof SurveyFormData] ?? field;
                    return (
                      <li key={field}>
                        <a
                          href={`#${id}`}
                          className="ds-link"
                          onClick={(event) => focusField(event, id)}
                        >
                          {message}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {stepComponentMap[currentStepId]}

            {submitError && currentStep === LAST_STEP && (
              <div className="ds-alert" data-color="danger" role="alert">
                <h2 className={`ds-heading ${classes.alertHeading}`} data-size="2xs">
                  {submitError.name || t('surveyCreation.wizard.submitErrorTitle')}
                </h2>
                <p className="ds-paragraph">{submitError.message}</p>
              </div>
            )}
          </div>

          <div className={classes.footer}>
            {currentStep > 0 ? (
              <button
                type="button"
                className="ds-button"
                data-variant="secondary"
                onClick={() => goToStep(currentStep - 1)}
              >
                {t('surveyCreation.buttons.previous')}
              </button>
            ) : (
              <span />
            )}
            <button
              type="submit"
              className="ds-button"
              data-variant="primary"
              disabled={currentStep === LAST_STEP && disableSendButton}
              aria-busy={currentStep === LAST_STEP && disableSendButton}
            >
              {currentStep === LAST_STEP
                ? submitLabel
                : t('surveyCreation.buttons.next')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
